(() => {
  if (window.AcProductCards) return;
  const events = new AbortController();
  const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const on = (target, type, listener) => target.addEventListener(type, listener, { signal: events.signal });
  const controllers = new WeakMap();
  const submittingForms = new WeakSet();
  let preloadObserver;
  let preloadTimer;

  const motionAllowed = () =>
    document.body?.dataset.acMotion !== 'reduced' && !motionPreference.matches;

  const pointerMotionSuspended = (card) => Boolean(card?.closest('[data-ac-product-rail-moving], [data-ac-product-list-moving]'));

  const captureImageState = (image) => {
    if (!image) return null;
    return {
      src: image.getAttribute('src'),
      srcset: image.getAttribute('srcset'),
      alt: image.getAttribute('alt'),
      width: image.getAttribute('width'),
      height: image.getAttribute('height'),
      objectPosition: image.style.objectPosition,
    };
  };

  const setOptionalAttribute = (element, name, value) => {
    if (value == null || value === '') element.removeAttribute(name);
    else element.setAttribute(name, value);
  };

  const applyImageState = (image, state) => {
    if (!image || !state) return;
    setOptionalAttribute(image, 'srcset', state.srcset);
    setOptionalAttribute(image, 'src', state.src);
    if (state.alt == null) image.removeAttribute('alt');
    else image.setAttribute('alt', state.alt);
    setOptionalAttribute(image, 'width', state.width);
    setOptionalAttribute(image, 'height', state.height);
    if (state.objectPosition) image.style.objectPosition = state.objectPosition;
    else image.style.removeProperty('object-position');
  };

  const applyVariantImage = (image, imageData) => {
    if (!image || !imageData?.src) return false;
    setOptionalAttribute(image, 'srcset', imageData.srcset);
    image.src = imageData.src;
    image.alt = imageData.alt || '';
    setOptionalAttribute(image, 'width', imageData.width);
    setOptionalAttribute(image, 'height', imageData.height);
    if (imageData.position) image.style.objectPosition = imageData.position;
    else image.style.removeProperty('object-position');
    return true;
  };

  const readVariant = (card) => {
    const source = card.querySelector('[data-ac-product-card-variant]');
    try { return JSON.parse(source?.textContent || 'null'); }
    catch { return null; }
  };

  const optionImage = (option) => option.dataset.variantImage
      ? {
          src: option.dataset.variantImage,
          srcset: option.dataset.variantImageSrcset,
          alt: option.dataset.variantImageAlt,
          position: option.dataset.variantImagePosition,
          width: option.dataset.variantImageWidth,
          height: option.dataset.variantImageHeight,
        }
      : null;

  class ProductCardController {
    constructor(card) {
      this.card = card;
      this.media = card.querySelector('[data-ac-product-card-hover]');
      this.primary = card.querySelector('[data-ac-product-card-primary]');
      this.secondary = card.querySelector('[data-ac-product-card-secondary]');
      this.secondaryTemplate = this.secondary?.querySelector('template[data-ac-product-card-secondary-image]');
      this.primaryImage = this.primary?.querySelector('img') || null;
      this.committedImage = captureImageState(this.primaryImage);
      this.committedVariant = readVariant(card);
      this.generation = 0;
      this.pending = false;
      this.destroyed = false;
      this.selectedOptions = this.committedVariant?.options?.slice() || [];
      this.previewing = false;
      this.mediaActive = false;
      this.mediaGeneration = 0;
      this.previewGeneration = 0;
      if (this.media) this.media.dataset.acCardMediaEnhanced = '';
    }

    updateAvailability() {
      this.card.querySelectorAll('[data-ac-product-card-option]').forEach((option) => {
        const available = option.dataset.variantAvailable === 'true';
        option.dataset.variantAvailable = String(available);
        option.classList.toggle('is-unavailable', !available);
        if (available) {
          option.removeAttribute('aria-disabled');
          option.removeAttribute('tabindex');
        } else {
          option.setAttribute('aria-disabled', 'true');
          option.setAttribute('tabindex', '-1');
        }
        option.setAttribute(
          'aria-label',
          available ? option.dataset.optionLabelAvailable || '' : option.dataset.optionLabelUnavailable || '',
        );
        const tooltipStatus = option.querySelector('[data-ac-product-card-option-tooltip-status]');
        if (tooltipStatus) tooltipStatus.hidden = available;
      });
      this.card.querySelectorAll('[data-ac-product-card-hide-unavailable]').forEach((group) => {
        const options = Array.from(group.querySelectorAll('[data-ac-product-card-option]'));
        const available = options.filter((option) => option.dataset.variantAvailable === 'true');
        const limit = Math.max(1, Number(group.dataset.optionLimit) || 5);
        const visible = available.slice(0, limit);
        const selected = available.find((option) => option.getAttribute('aria-current') === 'true');
        // Keep the committed value visible when it falls beyond the preview limit.
        if (selected && !visible.includes(selected)) visible[visible.length - 1] = selected;
        options.forEach((option) => {
          option.closest('.ac-product-card__option-item').hidden = !visible.includes(option);
        });
        const overflow = group.querySelector('[data-ac-product-card-option-overflow]');
        if (overflow) {
          const count = Math.max(0, available.length - limit);
          overflow.hidden = count === 0;
          overflow.textContent = (group.dataset.overflowLabel || '').replace('__count__', String(count));
        }
      });
    }

    stopMediaMotion() {
      if (!this.media || !this.primary || !this.secondary) return;
      this.mediaActive = false;
      this.mediaGeneration += 1;
      this.media.dataset.acCardMediaStatic = '';
      this.media.classList.remove('is-active');
      const video = this.media.querySelector('video');
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
    }

    suspendPointerMotion() {
      this.restorePreview();
      this.stopMediaMotion();
    }

    preloadSecondary() {
      if (!this.secondaryTemplate || this.destroyed) return null;
      let image = this.secondary.querySelector('img');
      if (!image) {
        image = this.secondaryTemplate.content.querySelector('img').cloneNode(true);
        image.loading = 'eager';
        image.fetchPriority = 'low';
      }
      // Detached/eager images cannot use auto sizing; retain the shared fallback.
      image.sizes = (this.primaryImage?.sizes || image.sizes).replace(/^auto,\s*/, '');
      if (!image.isConnected) this.secondaryTemplate.before(image);
      return image;
    }

    async enter() {
      if (!this.media || this.previewing || this.pending || this.mediaActive) return;
      this.mediaActive = true;
      const generation = ++this.mediaGeneration;
      const image = this.preloadSecondary();
      if (image) {
        try { await image.decode(); }
        catch {
          if (!image.naturalWidth) {
            if (generation === this.mediaGeneration) this.mediaActive = false;
            // A fresh element permits another request on the next intentional hover.
            if (image.complete) image.remove();
            return;
          }
        }
      }
      if (this.destroyed || !this.card.isConnected || generation !== this.mediaGeneration || this.previewing || this.pending) return;
      delete this.media.dataset.acCardMediaStatic;
      if (motionAllowed()) this.media.querySelector('video')?.play().catch(() => {});
      this.media.classList.add('is-active');
    }

    leave() {
      this.restorePreview();
      if (!this.media) return;
      this.mediaActive = false;
      this.mediaGeneration += 1;
      const video = this.media.querySelector('video');
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
      this.media.classList.remove('is-active');
    }

    async preview(option) {
      if (
        this.pending || this.card.dataset.acProductCardVariantMediaPreview === 'false' ||
        option.dataset.variantAvailable !== 'true' ||
        !option.dataset.variantId ||
        !this.primaryImage
      ) {
        return;
      }

      const imageData = optionImage(option);
      if (!imageData) return;
      this.stopMediaMotion();
      const image = new Image();
      image.sizes = this.primaryImage.sizes.replace(/^auto,\s*/, '');
      applyVariantImage(image, imageData);
      this.previewing = true;
      const generation = ++this.previewGeneration;
      try { await image.decode(); }
      catch {
        if (generation === this.previewGeneration) this.restorePreview();
        return;
      }
      if (this.destroyed || !this.card.isConnected || generation !== this.previewGeneration || this.pending) return;
      applyVariantImage(this.primaryImage, imageData);
      this.card.classList.add('is-option-previewing');
    }

    restorePreview() {
      if (!this.previewing) return;
      this.previewGeneration += 1;
      applyImageState(this.primaryImage, this.committedImage);
      this.previewing = false;
      this.card.classList.remove('is-option-previewing');
    }

    setPending(pending, url) {
      if (pending) {
        if (!this.pending) {
          this.pendingButtons = [...this.card.querySelectorAll('[data-ac-wishlist-toggle], [data-ac-product-card-quick-add-form] button')]
            .map((node) => ({ node, disabled: node.disabled }));
          this.pendingLinks = [...this.card.querySelectorAll('[data-ac-product-card-link], [data-ac-quick-add-open]')]
            .map((node) => ({ node, href: node.getAttribute('href'), productUrl: node.getAttribute('data-product-url') }));
        }
        this.pendingButtons.forEach(({ node }) => { node.disabled = true; });
        this.pendingLinks.forEach(({ node }) => {
          node.href = url;
          if (node.matches('[data-ac-quick-add-open]')) node.dataset.productUrl = url;
        });
        this.card.setAttribute('aria-busy', 'true');
      } else {
        this.pendingButtons?.forEach(({ node, disabled }) => { node.disabled = disabled; });
        this.pendingLinks?.forEach(({ node, href, productUrl }) => {
          setOptionalAttribute(node, 'href', href);
          setOptionalAttribute(node, 'data-product-url', productUrl);
        });
        this.pendingButtons = null;
        this.pendingLinks = null;
        this.pendingOptionValueIds = null;
        this.card.removeAttribute('aria-busy');
      }
      this.pending = pending;
    }

    async commit(option) {
      const generation = ++this.generation;
      this.request?.abort();
      this.request = new AbortController();
      const ids = (this.pendingOptionValueIds || this.committedVariant.optionValueIds).slice();
      ids[Number(option.dataset.optionPosition) - 1] = option.dataset.optionValueId;
      this.pendingOptionValueIds = ids;
      const url = new URL(this.card.dataset.acProductUrl, location.href);
      url.searchParams.delete('variant');
      url.searchParams.set('option_values', ids.join(','));
      this.restorePreview();
      this.stopMediaMotion();
      this.setPending(true, url.href);
      const status = this.card.querySelector('[data-ac-product-card-option-status]');
      if (status) status.textContent = '';
      cancelAnimationFrame(this.statusFrame);
      url.searchParams.set('section_id', 'ac-product-card-render');
      try {
        const response = await fetch(url, { headers: { Accept: 'text/html' }, signal: this.request.signal });
        if (!response.ok) throw new Error('Card option response unavailable');
        const html = await response.text();
        if (this.destroyed || generation !== this.generation || !this.card.isConnected) return;
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-ac-product-card]');
        const variant = fresh && readVariant(fresh);
        if (!variant?.id || fresh.dataset.acProductId !== this.card.dataset.acProductId
          || variant.optionValueIds?.length !== ids.length
          || variant.optionValueIds.some((id, index) => String(id) !== String(ids[index]))) throw new Error('Invalid card option response');
        const options = [...this.card.querySelectorAll('[data-ac-product-card-option]')];
        const next = [...fresh.querySelectorAll('[data-ac-product-card-option]')];
        const replacements = options.map((value) => next.find((item) => item.dataset.optionValueId === value.dataset.optionValueId));
        if (replacements.some((item) => !item)) throw new Error('Incomplete card options');
        options.forEach((value, index) => {
          const replacement = replacements[index];
          Object.keys(value.dataset).filter((key) => key.startsWith('variant')).forEach((key) => { delete value.dataset[key]; });
          Object.assign(value.dataset, replacement.dataset);
          value.href = replacement.getAttribute('href');
          const swatch = value.querySelector('.ac-product-card__swatch');
          if (swatch) swatch.style.cssText = replacement.querySelector('.ac-product-card__swatch')?.style.cssText || '';
        });
        this.card.querySelector('[data-ac-product-card-variant]').textContent = JSON.stringify(variant);
        this.setPending(false);
        this.applyVariant(option, variant);
        this.syncMerchandising(fresh);
      } catch (error) {
        if (this.destroyed || generation !== this.generation || error.name === 'AbortError') return;
        this.setPending(false);
        if (status) status.textContent = this.card.dataset.acProductOptionError || '';
      }
    }

    syncMerchandising(fresh) {
      const badge = this.card.querySelector('[data-ac-product-card-badge]');
      const nextBadge = this.card.dataset.acProductCardBadges === 'true'
        ? fresh.querySelector('[data-ac-product-card-badge]') : null;
      if (badge && nextBadge) {
        badge.className = nextBadge.className;
        if (nextBadge.hasAttribute('style')) badge.setAttribute('style', nextBadge.getAttribute('style'));
        else badge.removeAttribute('style');
        badge.textContent = nextBadge.textContent;
      } else if (badge) badge.remove();
      else if (nextBadge) this.card.querySelector('.ac-product-card__overlay')?.prepend(nextBadge.cloneNode(true));
      this.card.classList.toggle('ac-product-card--has-badge', Boolean(nextBadge));

      const inventory = this.card.querySelector('[data-ac-product-card-inventory]');
      const nextInventory = fresh.querySelector('[data-ac-product-card-inventory]');
      if (inventory && nextInventory) inventory.textContent = nextInventory.textContent;
      else if (inventory) inventory.remove();
      else if (nextInventory) this.card.querySelector('[data-ac-product-card-summary]')?.append(nextInventory.cloneNode(true));
    }

    cancelSelection() {
      this.destroyed = true;
      this.generation++;
      this.request?.abort();
      this.setPending(false);
    }

    applyVariant(option, variant) {

      this.restorePreview();
      this.stopMediaMotion();

      const optionPosition = Number(option.dataset.optionPosition) - 1;
      if (variant.options) this.selectedOptions = variant.options.slice();
      else if (optionPosition >= 0) this.selectedOptions[optionPosition] = option.dataset.optionValue;
      this.committedVariant = variant;
      this.card.dataset.acProductCardVariantId = String(variant.id);

      this.card.querySelectorAll('[data-ac-product-card-option]').forEach((value) => {
        const position = Number(value.dataset.optionPosition) - 1;
        const selected = this.selectedOptions[position] === value.dataset.optionValue;
        value.classList.toggle('is-selected', selected);
        if (selected) value.setAttribute('aria-current', 'true');
        else value.removeAttribute('aria-current');
      });

      const variantUrl = variant.url;
      if (variantUrl) {
        this.card.querySelectorAll('[data-ac-product-card-link]').forEach((link) => link.setAttribute('href', variantUrl));
      }

      const currentPrice = this.card.querySelector('[data-ac-product-card-current-price]');
      if (currentPrice && variant.price) currentPrice.textContent = variant.price;

      const price = this.card.querySelector('[data-ac-product-card-price]');
      const comparePrice = this.card.querySelector('[data-ac-product-card-compare-price]');
      const hasComparePrice = Boolean(comparePrice && variant.comparePrice);
      if (comparePrice) {
        comparePrice.textContent = variant.comparePrice || '';
        comparePrice.hidden = !hasComparePrice;
      }
      price?.classList.toggle('ac-product-card__price--sale', hasComparePrice);
      price?.querySelectorAll('[data-ac-product-card-price-label]').forEach((label) => {
        label.hidden = !hasComparePrice;
      });

      const unitPrice = this.card.querySelector('[data-ac-product-card-unit-price]');
      const unitPriceValue = unitPrice?.querySelector('[data-ac-product-card-unit-price-value]');
      if (unitPriceValue) {
        unitPriceValue.textContent = variant.unitPrice || '';
        unitPrice.hidden = !variant.unitPrice;
      }

      applyVariantImage(this.primaryImage, variant.image);

      const variantInput = this.card.querySelector('[data-ac-product-card-variant-input]');
      if (variantInput) variantInput.value = variant.id;

      const quickAddButton = this.card.querySelector('[data-ac-product-card-quick-add-form] button[type="submit"]');
      if (quickAddButton) quickAddButton.disabled = !variant.available;

      const quickAddOpener = this.card.querySelector('[data-ac-quick-add-open]');
      if (quickAddOpener && variantUrl) {
        quickAddOpener.href = variantUrl;
        quickAddOpener.dataset.productUrl = variantUrl;
      }

      const wishlist = this.card.querySelector('[data-ac-wishlist-toggle]');
      if (wishlist) {
        wishlist.dataset.acWishlistVariantId = variant.id;
        wishlist.dataset.acWishlistVariantTitle = variant.title || option.dataset.optionValue || '';
        if (this.selectedOptions.length) wishlist.dataset.acWishlistOptionValues = JSON.stringify(this.selectedOptions);
        wishlist.dataset.acWishlistUrl = variantUrl || wishlist.dataset.acWishlistUrl;
        wishlist.dataset.acWishlistImage = variant.image?.src || wishlist.dataset.acWishlistImage;
        wishlist.dataset.acWishlistPrice = variant.price || wishlist.dataset.acWishlistPrice;
        wishlist.dataset.acWishlistAvailable = String(variant.available);
        document.dispatchEvent(new CustomEvent('ac:wishlist:preview', { detail: { button: wishlist } }));
      }

      this.card.querySelectorAll('[data-ac-product-card-option-output]').forEach((output) => {
        const position = Number(output.dataset.optionPosition) - 1;
        const outputValue = output.querySelector('[data-ac-product-card-option-output-value]');
        const selectedValue = this.selectedOptions[position];
        if (outputValue && selectedValue) outputValue.textContent = selectedValue;
      });

      this.updateAvailability();

      const status = this.card.querySelector('[data-ac-product-card-option-status]');
      if (status) {
        status.textContent = '';
        cancelAnimationFrame(this.statusFrame);
        this.statusFrame = requestAnimationFrame(() => {
          if (this.card.isConnected && !events.signal.aborted) {
            status.textContent = option.getAttribute('aria-label') || option.dataset.optionValue || '';
          }
        });
      }

      this.committedImage = captureImageState(this.primaryImage);
      return true;
    }
  }

  const getController = (card) => {
    let controller = controllers.get(card);
    if (!controller) {
      controller = new ProductCardController(card);
      controllers.set(card, controller);
    }
    return controller;
  };

  const submitQuickAdd = async (form) => {
    if (submittingForms.has(form)) return;

    const button = form.querySelector('button[type="submit"]');
    const label = form.querySelector('[data-ac-product-card-quick-add-label]');
    const error = form.querySelector('[data-ac-product-card-quick-add-error]');
    if (!button || button.disabled) return;

    submittingForms.add(form);
    button.setAttribute('aria-disabled', 'true');
    button.classList.add('is-loading');
    if (label) label.textContent = form.dataset.addingLabel || label.textContent;
    if (error) {
      error.hidden = true;
      error.textContent = '';
    }

    try {
      if (!window.AcCartActions) throw new Error(form.dataset.errorLabel);
      await window.AcCartActions.addAndOpen(form, form.dataset.addedLabel || 'Added to bag');
    } catch (requestError) {
      const message = requestError.message || form.dataset.errorLabel;
      if (error && form.isConnected && !events.signal.aborted) {
        error.textContent = message;
        error.hidden = false;
        error.focus({ preventScroll: true });
      }
      window.AcCartActions?.announce(message);
    } finally {
      submittingForms.delete(form);
      button.removeAttribute('aria-disabled');
      button.classList.remove('is-loading');
      if (label) label.textContent = label.dataset.defaultLabel || label.textContent;
    }
  };

  on(document, 'pointerover', (event) => {
    if (event.pointerType === 'touch') return;

    const option = event.target.closest?.('[data-ac-product-card-option]');
    const optionCard = option?.closest('[data-ac-product-card]');
    if (pointerMotionSuspended(optionCard)) return;
    if (option && !option.contains(event.relatedTarget)) {
      getController(optionCard).preview(option);
      return;
    }

    const card = event.target.closest?.('[data-ac-product-card]');
    if (!card || pointerMotionSuspended(card) || card.contains(event.relatedTarget)) return;
    getController(card).enter();
  });

  on(document, 'pointerout', (event) => {
    if (event.pointerType === 'touch') return;

    const option = event.target.closest?.('[data-ac-product-card-option]');
    const optionCard = option?.closest('[data-ac-product-card]');
    if (pointerMotionSuspended(optionCard)) return;
    if (option && !option.contains(event.relatedTarget)) {
      getController(optionCard).restorePreview();
    }

    const card = event.target.closest?.('[data-ac-product-card]');
    if (!card || pointerMotionSuspended(card) || card.contains(event.relatedTarget) || card.matches(':focus-within')) return;
    getController(card).leave();
  });

  const suspendPointerCards = (event) => {
    if (!(event.target instanceof Element)) return;
    for (const card of event.target.querySelectorAll('[data-ac-product-card]')) {
      if (card.matches(':focus-within')) continue;
      controllers.get(card)?.suspendPointerMotion();
    }
  };

  const resumePointerCards = (event) => {
    if (!(event.target instanceof Element)) return;
    const hoveredCard = event.target.querySelector('[data-ac-product-card]:hover');
    if (hoveredCard) getController(hoveredCard).enter();
  };
  for (const source of ['rail', 'list']) {
    on(document, `ac:product-${source}-motion-start`, suspendPointerCards);
    on(document, `ac:product-${source}-motion-end`, resumePointerCards);
  }

  on(document, 'focusin', (event) => {
    const option = event.target.closest?.('[data-ac-product-card-option]');
    if (option) {
      getController(option.closest('[data-ac-product-card]')).preview(option);
      return;
    }

    const card = event.target.closest?.('[data-ac-product-card]');
    if (!card || card.contains(event.relatedTarget)) return;
    getController(card).enter();
  });

  on(document, 'focusout', (event) => {
    const option = event.target.closest?.('[data-ac-product-card-option]');
    if (option && (!event.relatedTarget || !option.contains(event.relatedTarget))) {
      getController(option.closest('[data-ac-product-card]')).restorePreview();
    }

    const card = event.target.closest?.('[data-ac-product-card]');
    if (!card || (event.relatedTarget && card.contains(event.relatedTarget)) || card.matches(':hover')) return;
    getController(card).leave();
  });

  on(document, 'click', (event) => {
    const option = event.target.closest?.('[data-ac-product-card-option]');
    if (!option) return;
    if ((event.button != null && event.button !== 0) || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const card = option.closest('[data-ac-product-card]');
    if (option.dataset.productUrl && new URL(option.dataset.productUrl, location.href).pathname !== new URL(card.dataset.acProductUrl, location.href).pathname) return;
    if (option.dataset.variantAvailable !== 'true') {
      event.preventDefault();
      return;
    }
    const controller = getController(card);
    if (card.dataset.acProductCardQuickAddMode !== 'drawer' || !controller.committedVariant?.optionValueIds?.every(Boolean)) return;
    event.preventDefault();
    controller.commit(option);
  });

  on(document, 'submit', (event) => {
    const form = event.target.closest?.('[data-ac-product-card-quick-add-form]');
    if (!form) return;
    event.preventDefault();
    submitQuickAdd(form);
  });

  const cardsWithin = (scope) => {
    const cards = [...scope.querySelectorAll('[data-ac-product-card]')];
    if (scope instanceof Element && scope.matches('[data-ac-product-card]')) cards.unshift(scope);
    return cards;
  };

  const destroyWithin = (scope) => {
    window.ACPreload?.forget(scope);
    cardsWithin(scope).forEach((card) => {
      preloadObserver?.unobserve(card);
      const controller = controllers.get(card);
      if (!controller) return;
      cancelAnimationFrame(controller.statusFrame);
      controller.cancelSelection();
      controller.restorePreview();
      controller.stopMediaMotion();
      controllers.delete(card);
    });
  };

  const observeSecondary = (scope) => {
    window.ACPreload?.scan(scope);
    if (!preloadObserver) return;
    for (const card of cardsWithin(scope)) {
      if (card.querySelector('[data-ac-product-card-hover] template[data-ac-product-card-secondary-image]')) {
        preloadObserver.observe(card);
      }
    }
  };

  const startPreload = () => {
    if (preloadObserver || document.hidden || !window.IntersectionObserver
      || !window.matchMedia('(any-hover: hover)').matches) return;
    preloadObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting || !entry.target.isConnected) continue;
        preloadObserver.unobserve(entry.target);
        getController(entry.target).preloadSecondary();
      }
    }, { rootMargin: '200px' });
    observeSecondary(document);
  };
  const schedulePreload = () => {
    clearTimeout(preloadTimer);
    preloadTimer = setTimeout(startPreload, 1000);
  };
  if (document.readyState === 'complete') schedulePreload();
  else on(window, 'load', schedulePreload);
  on(document, 'visibilitychange', () => {
    if (!document.hidden && document.readyState === 'complete') schedulePreload();
  });
  on(window, 'pageshow', (event) => {
    if (event.persisted) observeSecondary(document);
  });

  const syncMotion = () => {
    if (motionAllowed()) return;
    cardsWithin(document).forEach((card) => {
      const controller = controllers.get(card);
      if (!controller) return;
      controller.stopMediaMotion();
      if (controller.media && !controller.previewing && card.matches(':hover, :focus-within')) {
        controller.enter();
      }
    });
  };
  const observer = new MutationObserver((records) => {
    if (records.some((record) => record.type === 'attributes')) syncMotion();
    for (const record of records) for (const node of record.addedNodes) {
      if (node instanceof Element) observeSecondary(node);
    }
    records.forEach((record) => record.removedNodes.forEach((node) => {
      if (node instanceof Element && !node.isConnected) destroyWithin(node);
    }));
  });
  observer.observe(document.body, { attributes: true, attributeFilter: ['data-ac-motion'], childList: true, subtree: true });
  on(motionPreference, 'change', syncMotion);
  on(document, 'shopify:section:unload', (event) => destroyWithin(event.target));
  const destroy = () => {
    events.abort();
    clearTimeout(preloadTimer);
    preloadObserver?.disconnect();
    observer.disconnect();
    destroyWithin(document);
    delete window.AcProductCards;
  };
  on(window, 'pagehide', (event) => {
    if (event.persisted) destroyWithin(document);
    else destroy();
  });
  window.AcProductCards = { destroyWithin, destroy, preload: (card) => getController(card).preloadSecondary() };
})();
